/**
 * Digital Marketing services overview page
 * Converted blue accents to orange
 */
import { Link } from 'react-router'
import { Button } from '../../components/ui/button'
import { TrendingUp, Users, Target, Globe, ArrowRight } from 'lucide-react'

export default function DigitalMarketingPage() {
  return (
    <div>
      {/* Hero Section (orange) */}
      <section className="bg-gradient-to-br from-orange-900 via-orange-800 to-orange-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">Digital Marketing Services</h1>
              <p className="text-xl text-orange-100 leading-relaxed mb-8">
                Comprehensive digital solutions to elevate your brand presence, engage your audience, 
                and drive measurable results across all digital platforms.
              </p>
              <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
                <Link to="/contact">Start Your Digital Journey</Link>
              </Button>
            </div>
            <div>
              <img 
                src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/807b36eb-871c-4588-b838-2181356402f5.jpg" 
                alt="Digital Marketing Services"
                className="w-full h-80 object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Digital Marketing Services</h2>
            <p className="text-[12px] text-gray-600 max-w-3xl mx-auto text-justify">
              Edicoo specializes in crafting and executing comprehensive marketing strategies tailored to the online realm, drawing from the evolving landscape of digital platforms and consumer behavior. We focus on leveraging various techniques such as content marketing, influencer collaborations, social media engagement, e-commerce promotions, SEM, campaign-based Meta (Facebook - Instagram) and Google (YouTube- Google Display) paid ads. Our primary objective is to help businesses establish a strong digital presence, engage with target audiences effectively, and drive successful marketing campaigns across the diverse spectrum of online platforms.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Influencer Marketing */}
            <div className="bg-gradient-to-br from-purple-50 to-orange-50 p-8 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Influencer Marketing</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Strategic partnerships with key influencers to authentically connect with your target audience.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-purple-200 text-purple-700 hover:bg-purple-50">
                <Link to="/services/influencer-marketing">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>

            {/* Paid Advertising */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Paid Advertising</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Maximize ROI with targeted Google Ads, Meta Ads, and other paid advertising platforms.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-green-200 text-green-700 hover:bg-green-50">
                <Link to="/services/paid-advertising">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>

            {/* Social Media Management */}
            <div className="bg-gradient-to-br from-orange-50 to-rose-50 p-8 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Globe className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Social Media Management</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Complete social media strategy, content planning, and community management.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-orange-200 text-orange-700 hover:bg-orange-50">
                <Link to="/services/social-media">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>

            {/* Content Creation */}
            <div className="bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <TrendingUp className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Content Creation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Creative content across all formats - video, graphics, copy, and multimedia.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-orange-200 text-orange-700 hover:bg-orange-50">
                <Link to="/services/content-creation">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>

            {/* Translation & Adaptation */}
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-8 rounded-2xl hover:shadow-lg transition-shadow md:col-span-2 lg:col-span-1">
              <div className="bg-violet-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Globe className="h-8 w-8 text-violet-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Translation & Adaptation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Cultural adaptation and localization services, especially for Turkish markets.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-violet-200 text-violet-700 hover:bg-violet-50">
                <Link to="/services/translation">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
